import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CloudinaryEditorModalProps {
  isOpen: boolean;
  imageUrl: string;
  onSave: (croppedUrl: string) => void;
  onClose: () => void;
  title?: string;
}

export const CloudinaryEditorModal: React.FC<CloudinaryEditorModalProps> = ({
  isOpen,
  imageUrl,
  onSave,
  onClose,
  title = 'Edit Image'
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Handle postMessage from iframe
  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      // Verify origin if needed
      // if (event.origin !== 'https://cloudinary-canvas-studio.lovable.app') return;

      if (event.data.type === 'SAVE_IMAGE' && event.data.croppedUrl) {
        onSave(event.data.croppedUrl);
        onClose();
      } else if (event.data.type === 'CANCEL') {
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen, onSave, onClose]);

  // Update iframe src when imageUrl changes
  useEffect(() => {
    if (isOpen && iframeRef.current && imageUrl) {
      const iframeSrc = `https://cloudinary-canvas-studio.lovable.app/?url=${encodeURIComponent(imageUrl)}&embed=true`;
      if (iframeRef.current.src !== iframeSrc) {
        iframeRef.current.src = iframeSrc;
      }
    }
  }, [isOpen, imageUrl]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Cleanup iframe on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear iframe src to help browser garbage collect
      if (iframeRef.current) {
        iframeRef.current.src = 'about:blank';
      }
    };
  }, []);

  if (!isOpen) return null;

  const iframeSrc = `https://cloudinary-canvas-studio.lovable.app/?url=${encodeURIComponent(imageUrl)}&embed=true`;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-8 w-8 p-0"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Iframe Content */}
      <div className="flex-1 overflow-hidden">
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          className="w-full h-full border-none"
          allow="clipboard-write"
          title={title}
        />
      </div>
    </div>
  );
};
